import { useMemo } from "react";
import noop from "lodash/noop";

import { BotActions } from "./BotActions";
import { BotMessage } from "./BotMessage";
import { BotResponse } from "./BotResponse";
import { CopyBotMessage } from "./CopyBotMessage";
import { RunZapBotMessage } from "./RunZapBotMessage";
import { UserMessage } from "./UserMessage";
import { UserPrompt, UserPromptProps } from "./UserPrompt";

import { Badge } from "./components";
import { useBlockDisplayStyle } from "./hooks";
import { cn } from "./utils";

import { useChatbotContext } from "./ChatbotContext";
import { STRINGS } from "./constants";

const StyledChatbot: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div"> & {
    $blockHeights: { height: string; maxHeight: string };
  }
> = ({ $blockHeights, ...props }) => (
  <div
    {...props}
    style={{ height: $blockHeights.height, maxHeight: $blockHeights.maxHeight }}
    className={cn("flex flex-col justify-end gap-5", props.className)}
  />
);

const StyledMessagesScroll: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div
    {...props}
    className={cn("flex flex-col-reverse overflow-y-auto", props.className)}
  />
);

const StyledMessages: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div
    {...props}
    className={cn("flex flex-col gap-5 px-5 pt-5", props.className)}
  />
);

export function EditingChatbotPrompt() {
  const {
    chatbotName,
    defaultMessage,
    defaultPrompt,
    directive,
    disclosureText,
    displayDisclosure,
    isStaticMode,
    model,
    placeholder,
    style,
    triggers,
  } = useChatbotContext();

  const blockDisplayStyle = useBlockDisplayStyle(style);

  const userPromptProps = useMemo<UserPromptProps>(
    () => ({
      chatbotName,
      isDisabled: true,
      messages: [],
      model,
      placeholder,
      onChatComplete: noop,
      onUsage: noop,
    }),
    [chatbotName, model, placeholder]
  );

  const renderDemoMessages = () =>
    triggers.length > 0 && (
      <>
        <UserMessage>
          <Badge>
            {STRINGS["Prompt input provided to the response above"]}
          </Badge>
        </UserMessage>
        <BotMessage chatbotName={chatbotName}>
          <BotResponse>
            <Badge>
              {STRINGS["Response generated to the prompt input above"]}
            </Badge>
          </BotResponse>
          {triggers.length > 0 && (
            <BotActions>
              {triggers.map((trigger) =>
                trigger.onClick === "copy" ? (
                  <CopyBotMessage
                    key={trigger.id}
                    buttonText={trigger.buttonText}
                    message={{
                      content: STRINGS["This is a demo response"],
                    }}
                    onClick={noop}
                  />
                ) : (
                  <RunZapBotMessage
                    key={trigger.id}
                    buttonText={trigger.buttonText}
                    message={{
                      content: STRINGS["This is a demo response"],
                    }}
                    onClick={noop}
                  />
                )
              )}
            </BotActions>
          )}
        </BotMessage>
      </>
    );

  return (
    <StyledChatbot $blockHeights={blockDisplayStyle}>
      <StyledMessagesScroll>
        <StyledMessages>
          {isStaticMode ? (
            <>
              <BotMessage chatbotName={chatbotName} isHidden>
                <BotResponse>{directive}</BotResponse>
              </BotMessage>
              <BotMessage
                chatbotName={chatbotName}
                displayDisclosure={displayDisclosure}
                disclosureText={disclosureText}
              >
                <BotResponse>{defaultMessage}</BotResponse>
              </BotMessage>
              {renderDemoMessages()}
              <UserPrompt {...userPromptProps} />
            </>
          ) : (
            <>
              <BotMessage chatbotName={chatbotName} isHidden>
                <BotResponse>{directive}</BotResponse>
              </BotMessage>
              <UserMessage isHidden>{defaultPrompt}</UserMessage>
              <BotMessage
                chatbotName={chatbotName}
                displayDisclosure={displayDisclosure}
                disclosureText={disclosureText}
              >
                <BotResponse>
                  <Badge>{STRINGS["Greeting generated from prompt"]}</Badge>
                </BotResponse>
              </BotMessage>
              {renderDemoMessages()}
              <UserPrompt {...userPromptProps} />
            </>
          )}
        </StyledMessages>
      </StyledMessagesScroll>
    </StyledChatbot>
  );
}
